import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Instagram, 
  Facebook, 
  Linkedin, 
  Twitter, 
  MapPin, 
  ArrowUpRight,
  ChevronRight,
  LayoutGrid,
  Zap,
  PlayCircle,
  FolderArchive,
  MessageSquare,
  ArrowUp
} from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  const socialLinks = [
    { name: "Instagram", icon: Instagram, href: "#" },
    { name: "LinkedIn", icon: Linkedin, href: "#" },
    { name: "Facebook", icon: Facebook, href: "#" },
    { name: "Twitter", icon: Twitter, href: "#" },
  ];

  const quickLinks = [
    { name: "Products", path: "/#products", icon: LayoutGrid },
    { name: "Solutions", path: "/solutions", icon: Zap },
    { name: "Videos", path: "/videos", icon: PlayCircle },
    { name: "Project Archive", path: "/gallery", icon: FolderArchive },
    { name: "Contact", path: "/contact", icon: MessageSquare },
  ];

  return (
    <footer className="relative bg-black border-t border-white/20 pt-24 pb-12 overflow-hidden">
      {/* Enhanced Top Glow for separation */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[1px] bg-gradient-to-r from-transparent via-white/40 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-20">
          
          {/* Column 1: Brand */}
          <div className="space-y-6">
            <Link to="/" onClick={scrollToTop} aria-label="BA Tents Home">
              <img
                src="https://res.cloudinary.com/dizdlq9fo/image/upload/v1774872220/Logo-With-Name_kujnir.png"
                alt="BA Tents Logo"
                width={300}
                height={84}
                className="h-12 w-auto object-contain mb-4 brightness-110"
                loading="eager"
                decoding="async"
              />
            </Link>
            {/* Brightened text to zinc-300 */}
            <p className="text-zinc-300 text-sm leading-relaxed font-normal max-w-xs">
              Engineering excellence in temporary and permanent structural solutions. Redefining space across the GCC for over three decades.
            </p>
          </div>

          {/* Column 2: Locations */}
          <div>
            {/* Headers are now full white for maximum contrast */}
            <p className="text-white text-[11px] font-black uppercase tracking-[0.3em] mb-8">Global Presence</p>
            <ul className="space-y-6">
              {[
                { city: "Dubai, UAE", type: "Corporate HQ" },
                { city: "Abu Dhabi, UAE", type: "Branch Office" },
                { city: "Riyadh, KSA", type: "Regional Office" }
              ].map((loc, i) => (
                <li key={i} className="group cursor-default">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-white mt-0.5" />
                    <div>
                      <p className="text-white text-sm font-semibold">{loc.city}</p>
                      <p className="text-zinc-300 text-[11px] uppercase tracking-wider mt-1 font-medium">{loc.type}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Quick Links */}
          <div>
            <p className="text-white text-[11px] font-black uppercase tracking-[0.3em] mb-8">Navigation</p>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    onClick={scrollToTop}
                    className="text-zinc-300 hover:text-white text-sm transition-all flex items-center gap-3 group font-medium"
                  >
                    <link.icon className="w-4 h-4 text-zinc-100" />
                    {link.name}
                    <ChevronRight className="w-3 h-3 ml-auto opacity-100 text-white" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Social Media */}
          <div>
            <p className="text-white text-[11px] font-black uppercase tracking-[0.3em] mb-8">Connect</p>
            <div className="grid grid-cols-2 gap-3">
              {socialLinks.map((social) => (
                <a 
                  key={social.name}
                  href={social.href}
                  aria-label={social.name}
                  className="flex items-center justify-between px-4 py-3 border border-white/20 bg-white/10 rounded-xl hover:bg-white/20 hover:border-white/40 transition-all group"
                >
                  <social.icon className="w-4 h-4 text-white" />
                  <ArrowUpRight className="w-3 h-3 text-white/50 group-hover:text-white transition-all" />
                </a>
              ))}
            </div>
            <p className="mt-8 text-[11px] text-zinc-300 font-medium leading-relaxed italic">
              Follow our latest projects and structural innovations across the region.
            </p>
          </div>

        </div>

        {/* Bottom Copyright Bar */}
        <div className="pt-10 border-t border-white/20 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
            <p className="text-[10px] text-zinc-200 tracking-[0.2em] uppercase font-bold text-center md:text-left">
              © {currentYear} BA TENTS. ALL RIGHTS RESERVED.
            </p>
            <button 
              onClick={scrollToTop}
              className="flex items-center gap-2 text-[10px] text-white hover:underline transition-colors tracking-[0.2em] uppercase font-black group"
            >
              Back to Top
              <ArrowUp className="w-3 h-3" />
            </button>
          </div>
          
          <div className="flex gap-8">
            <Link to="/privacy" onClick={scrollToTop} className="text-[10px] text-zinc-200 hover:text-white transition-colors uppercase tracking-[0.2em] font-bold">Privacy</Link>
            <Link to="/terms" onClick={scrollToTop} className="text-[10px] text-zinc-200 hover:text-white transition-colors uppercase tracking-[0.2em] font-bold">Terms</Link>
          </div>
        </div>
      </div>

      {/* Decorative Gradient Blur - Opacity increased slightly for vibrance */}
      <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-[#bd6aff]/10 blur-[100px] rounded-full pointer-events-none" />
    </footer>
  );
};

export default Footer;